import { Component, OnDestroy } from '@angular/core';
import { FormGroup } from '@angular/forms';
import { DomSanitizer, SafeResourceUrl } from '@angular/platform-browser';
import { TranslatePipe, TranslateService } from '@ngx-translate/core';
import { MessageService } from 'primeng/api';
import { filter, Subject, take, takeUntil } from 'rxjs';
import { PATHS } from 'src-ironside/app-routing.module';
import { TypeResource } from 'src-ironside/enums/resource.enum';
import { ResourceDataState, ResourceType } from 'src-ironside/interfaces/resource.interface';
import { ResourceRepository } from 'src-ironside/repositories/resource.repository';
import { PermissionService } from 'src-ironside/services/permission.service';
import { BreadCrumbItem } from 'src-ironside/shared/components/bread-crumb/bread-crumb.component';

@Component({
    selector: 'app-chiffre-page',
    templateUrl: './chiffre-page.component.html',
    styleUrls: ['./chiffre-page.component.css'],
    providers: [ResourceRepository, MessageService],
})
export class ChiffrePageComponent implements OnDestroy {
    readonly unsubscribe$ = new Subject();

    photoState: ResourceDataState = {
        data: [],
        total: 0,
        page: 1,
        pageSize: 30,
    };

    commercialInformationState: ResourceDataState = {
        data: [],
        total: 0,
        page: 1,
        pageSize: 30,
    };

    videoState: ResourceDataState = {
        data: [],
        total: 0,
        page: 1,
        pageSize: 30,
    };

    activeIndex = 0;
    visibleItem: boolean = false;
    itemPdfShow: any;
    pdfLoaded: boolean = false;
    formPagination!: FormGroup;
    imgShow: string;
    videoShow: string;
    defaultImg = 'src-ironside/assets/images/image-default.jpg';

    constructor(
        private resourceRespository: ResourceRepository,
        private sanitizer: DomSanitizer,
        private translate: TranslateService,
        private _messageService: MessageService,
        private translateService: TranslatePipe,
        public permissionService: PermissionService
    ) {}

    ngOnInit(): void {
        this.permissionService.permissionsLoaded$
            .pipe(
                filter((loaded) => loaded),
                take(1)
            )
            .subscribe(() => {
                const priority = [
                    { id: 'flyers', permission: 'export-informations-commerciales' },
                    { id: 'photos', permission: 'export-photos-hd' },
                    { id: 'videos', permission: 'export-ressource-videos' },
                ];

                for (const item of priority) {
                    if (this.permissionService.isPermissionFonction(item.permission)) {
                        const index = this.visibleTabs.findIndex((tab) => tab.id === item.id);
                        if (index !== -1) {
                            this.activeIndex = index;
                            this.onTabChange(this.activeIndex);
                            break;
                        }
                    }
                }
            });
    }

    tabs = [
        { id: 'flyers', permission: 'export-informations-commerciales' },
        { id: 'photos', permission: 'export-photos-hd' },
        { id: 'videos', permission: 'export-ressource-videos' },
    ];

    get visibleTabs() {
        return this.tabs.filter((tab) =>
            this.permissionService.isPermissionFonction(tab.permission)
        );
    }

    get breadCrumb() {
        let breadCrumbs: BreadCrumbItem[] = [
            { name: this.translate.instant('Menu.home'), url: PATHS.home_en },
            { name: this.translate.instant('Menu.downloads') },
        ];
        return breadCrumbs;
    }

    fetchResourceByType(type: TypeResource, page: number, pageSize: number) {
        const filters: any = [
            {
                fieldName: 'availableOnTheDownloadPage',
                conditions: [
                    {
                        type: 'EQUALS',
                        value: true,
                    },
                ],
            },
            {
                fieldName: 'typeOfResource',
                conditions: [
                    {
                        type: 'EQUALS',
                        value: type,
                    },
                ],
            },
        ];

        if (type === TypeResource.VIDEO) {
            filters.push({
                fieldName: 'urlYoutube',
                conditions: [
                    {
                        type: 'IS_NOT_EMPTY',
                        value: '',
                    },
                ],
            });
        }

        const body = {
            collectionName: 'dynamic_ressource',
            filters: filters,
            sorts: { creationDate: 'desc' },
            page: page,
            pageSize: pageSize,
            timezone: 7,
        };
        this.resourceRespository
            .getDynamicResource(body, true)
            .pipe(takeUntil(this.unsubscribe$))
            .subscribe({
                next: (res) => {
                    switch (type) {
                        case TypeResource.PI_COM:
                            this.commercialInformationState = {
                                ...this.commercialInformationState,
                                data: res?.content,
                                total: res?.totalElements,
                            };

                            break;

                        case TypeResource.PHOTO:
                            this.photoState = {
                                ...this.photoState,
                                data: res?.content,
                                total: res?.totalElements,
                            };
                            break;

                        case TypeResource.VIDEO:
                            this.videoState = {
                                ...this.videoState,
                                data: res?.content,
                                total: res?.totalElements,
                            };
                            break;
                    }
                },
            });
    }

    sanitizeUrl(url: string): SafeResourceUrl {
        if (!url) return this.sanitizer.bypassSecurityTrustResourceUrl('');

        let videoId = '';
        const youtubeRegex = /(?:youtu\.be\/|youtube\.com\/(?:.*v=|embed\/))([^&?/]+)/;

        const match = url.match(youtubeRegex);
        if (match) {
            videoId = match[1];
        }

        if (!videoId) return this.sanitizer.bypassSecurityTrustResourceUrl('');

        const embedUrl = `https://www.youtube.com/embed/${videoId}`;
        return this.sanitizer.bypassSecurityTrustResourceUrl(embedUrl);
    }

    onTabChange(event: number) {
        const tab = this.visibleTabs[event];
        if (!tab) return;
        window.scrollTo(0, 0);
        switch (tab.id) {
            case 'flyers':
                this.fetchResourceByType(
                    TypeResource.PI_COM,
                    this.commercialInformationState.page,
                    this.commercialInformationState.pageSize
                );
                break;

            case 'photos':
                this.fetchResourceByType(
                    TypeResource.PHOTO,
                    this.photoState.page,
                    this.photoState.pageSize
                );
                break;

            case 'videos':
                this.fetchResourceByType(
                    TypeResource.VIDEO,
                    this.videoState.page,
                    this.videoState.pageSize
                );
                break;
        }
    }

    openFile(item?: ResourceType) {
        if ((item?.file && item.file?.length > 0) || item?.urlYoutube) {
            if (item.urlYoutube) {
                this.videoShow = item.urlYoutube;
            } else {
                const toPdf = { urlFile: item?.file?.[0]?.url };
                this.imgShow = item?.file?.[0]?.url!;
                this.itemPdfShow = toPdf;
            }
            setTimeout(() => {
                this.visibleItem = true;
            }, 0);
        } else {
            this._messageService.add({
                severity: 'warn',
                detail: this.translateService.transform('common.an-error-occurred'),
            });
        }
    }

    pageProgress(_event: any) {
        this.pdfLoaded = false;
    }

    pageRendered(_event: any) {
        this.pdfLoaded = true;
    }

    onChangePagination(index: number) {
        this.onTabChange(index);
    }

    getNameFile(item: any) {
        const urlFile = item?.file?.[0]?.url.split(
            'https://dev-ironside-elliance-minio.geolane.fr/resource-files/'
        )[1];
        const regex = /([^\/_]+\.[^\/?]+)(?=\?X-Amz|$)/;
        const match = urlFile?.match(regex);
        return match ? decodeURIComponent(match[1]) : undefined;
    }

    ngOnDestroy(): void {
        this.unsubscribe$.next(null);
        this.unsubscribe$.complete();
    }
}
